// scheduling/pages/ScheduleHistoryModal.jsx
// Visual audit history — v3
// Two modes over rprs.ScheduleEntries_Audit:
//   job    ({ jobId })   — everything that happened on one job, incl. its placeholders
//   person ({ staffId }) — everything that happened to one person's schedule, client AND
//                          custom jobs, plus the placeholders they were assigned from / moved to
// Grouped by BatchId (one save = one event), color-coded, consolidated MOVE+UPDATE per entry,
// rich summaries, job snapshot stats (job mode), full display names.
//
// Placeholder rows (migrate-081) have EntryId NULL + PlaceholderId set, and their own actions
// (PH_CREATE / PH_UPDATE / PH_DELETE / PH_JOB_CHANGE). Old/NewDate are the placeholder's start,
// Old/NewEndDate its end, Old/NewHours its total.

import { useState, useEffect } from 'react';
import { fetchScheduleHistory } from '../services/schedData';
import { BRAND } from '../utils/schedConstants';

const ACTION_COLORS = {
  INSERT:     { bg: '#D1FAE5', text: '#065F46', icon: '+',  label: 'Created' },
  UPDATE:     { bg: '#DBEAFE', text: '#1D4ED8', icon: '~',  label: 'Updated' },
  MOVE:       { bg: '#FFF7ED', text: '#C2410C', icon: '→',  label: 'Moved' },
  REASSIGN:   { bg: '#FEF3C7', text: '#92400E', icon: '↔',  label: 'Staff Reassigned' },
  JOB_CHANGE: { bg: '#EDE9FE', text: '#6D28D9', icon: '⇄',  label: 'Job Change' },
  DELETE:     { bg: '#FEE2E2', text: '#DC2626', icon: '−',  label: 'Removed' },
  PH_CREATE:     { bg: '#F1F5F9', text: '#475569', icon: '◇', label: 'Placeholder Created' },
  PH_UPDATE:     { bg: '#F1F5F9', text: '#475569', icon: '◇', label: 'Placeholder Updated' },
  PH_DELETE:     { bg: '#F1F5F9', text: '#64748B', icon: '◇', label: 'Placeholder Removed' },
  PH_JOB_CHANGE: { bg: '#F1F5F9', text: '#6D28D9', icon: '◇', label: 'Placeholder Job Change' },
};

const SOURCE_LABELS = {
  'ui': 'Manual',
  'edit': 'Edit',
  'create': 'Created',
  'suggestion': 'Staff Suggestion',
  'drag-drop': 'Drag & Drop',
  'conflict-resolve': 'Conflict Resolution',
  'reassign': 'Edit',           // legacy: early edit-modal reassigns used 'reassign' as source
  'job-change': 'Edit',         // legacy: early edit-modal job changes used 'job-change' as source
  'job-reassign': 'Job Reassign',
  'roll-forward': 'Roll Forward',
  'empire-sync': 'Empire Sync',
  'placeholder': 'Placeholder',
  'placeholder-merge': 'Placeholder Merge',
  'placeholder-assign': 'Assigned from Placeholder',
  'placeholder-convert': 'Moved to Placeholder',
  'pool-send': 'Sent to Pool',
  'closed-job-cleanup': 'Closed-Job Cleanup',
  'pe-sync-repoint': 'PE Sync (job re-keyed)',
  'pe-sync-dupe': 'PE Sync (duplicate removed)',
  'restore': 'Restore',
};

const PH_ACTIONS = new Set(['PH_CREATE', 'PH_UPDATE', 'PH_DELETE', 'PH_JOB_CHANGE']);
const isPh = (r) => r.PlaceholderId != null || PH_ACTIONS.has(r.Action);

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// DATE values arrive as UTC-midnight ISO strings; format in UTC so a Central-time browser
// doesn't show the previous day.
function fmtDateShort(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

// Compute date range string from sorted ISO dates
function dateRange(dates) {
  if (!dates.length) return '—';
  const sorted = [...dates].sort();
  if (sorted.length === 1) return fmtDateShort(sorted[0]);
  return `${fmtDateShort(sorted[0])} – ${fmtDateShort(sorted[sorted.length - 1])}`;
}

// Round hours to 2 decimals for display (drops trailing zeros so .25/.5/.75 show exactly)
function rh(n) { return Math.round((n || 0) * 100) / 100; }

// "Client / Job" for a real-job row, the custom job's name for a custom row.
function jobLabelOf(r) {
  if (!r) return '';
  if (r.CustomJobId != null) return r.CustomJobName || r.JobName || 'Internal job';
  if (r.ClientName && r.JobName) return `${r.ClientName} / ${r.JobName}`;
  return r.JobName || '';
}

function phLevel(r) {
  const dept = r.NewDepartmentCode || r.OldDepartmentCode || '';
  const lvl = r.NewLevel || r.OldLevel || '';
  return `${dept} ${lvl}`.trim() || 'placeholder';
}

function phRange(r, side) {
  const s = side === 'old' ? r.OldDate : r.NewDate;
  const e = side === 'old' ? r.OldEndDate : r.NewEndDate;
  if (!s) return '—';
  return e && String(e).slice(0, 10) !== String(s).slice(0, 10) ? `${fmtDateShort(s)} – ${fmtDateShort(e)}` : fmtDateShort(s);
}

// ── Placeholder batch summary ────────────────────────────────────────────────
function summarizePlaceholderBatch(rows, source) {
  const ph = rows.filter(isPh);
  const entries = rows.filter(r => !isPh(r));
  const p = ph[0];
  if (source === 'placeholder-assign' && entries.length) {
    const staff = entries[0]?.StaffName || 'staff';
    const dates = entries.filter(r => r.NewDate).map(r => r.NewDate);
    const total = entries.reduce((s, r) => s + ((r.NewHours || 0) - (r.OldHours || 0)), 0);
    return `${phLevel(p)} placeholder (${rh(p.OldHours)}h) assigned to ${staff}, ${dateRange(dates)} (${entries.length} entries, ${rh(total)}h)`;
  }
  if (source === 'placeholder-convert' && entries.length) {
    const staff = entries[0]?.StaffName || 'staff';
    const total = entries.reduce((s, r) => s + (r.OldHours || 0), 0);
    const merged = p && p.Action === 'PH_UPDATE';
    return `${staff}'s ${rh(total)}h moved to a ${phLevel(p)} placeholder${merged ? ` (merged into an existing one, now ${rh(p.NewHours)}h)` : ''}`;
  }
  if (source === 'placeholder-merge' && ph.length >= 2) {
    const target = ph.find(r => r.Action === 'PH_UPDATE') || ph[0];
    return `Placeholders merged: ${phLevel(target)} now ${rh(target.NewHours)}h, ${phRange(target, 'new')}`;
  }
  if (!p) return `${rows.length} changes`;
  if (p.Action === 'PH_CREATE') return `Placeholder created: ${phLevel(p)}, ${rh(p.NewHours)}h, ${phRange(p, 'new')}`;
  if (p.Action === 'PH_DELETE') return `Placeholder removed: ${phLevel(p)}, ${rh(p.OldHours)}h, ${phRange(p, 'old')}`;
  if (p.Action === 'PH_JOB_CHANGE') return `Placeholder ${phLevel(p)} moved to another job`;
  const parts = [];
  const oldLvl = `${p.OldDepartmentCode || ''} ${p.OldLevel || ''}`.trim();
  const newLvl = `${p.NewDepartmentCode || ''} ${p.NewLevel || ''}`.trim();
  if (oldLvl !== newLvl) parts.push(`${oldLvl} → ${newLvl}`);
  if (rh(p.OldHours) !== rh(p.NewHours)) parts.push(`${rh(p.OldHours)}h → ${rh(p.NewHours)}h`);
  if (phRange(p, 'old') !== phRange(p, 'new')) parts.push(`${phRange(p, 'old')} → ${phRange(p, 'new')}`);
  return `Placeholder ${phLevel(p)} updated${parts.length ? `: ${parts.join(', ')}` : ''}`;
}

// ── Rich batch summary ──────────────────────────────────────────────────────
function summarizeBatch(rows, source) {
  if (rows.some(isPh)) return summarizePlaceholderBatch(rows, source);

  const actions = new Set(rows.map(r => r.Action));
  const entryCount = new Set(rows.map(r => r.EntryId)).size;

  // All INSERTs — new entries created
  if (actions.size === 1 && actions.has('INSERT')) {
    const staff = rows[0]?.StaffName || 'staff';
    const dates = rows.filter(r => r.NewDate).map(r => r.NewDate).sort();
    const totalHrs = rows.reduce((s, r) => s + (r.NewHours || 0), 0);
    return `Assigned to ${staff}, ${dateRange(dates)} (${entryCount} entries, ${rh(totalHrs)}h)`;
  }

  // All DELETEs — entries removed
  if (actions.size === 1 && actions.has('DELETE')) {
    const staff = rows[0]?.StaffName || 'staff';
    const totalHrs = rows.reduce((s, r) => s + (r.OldHours || 0), 0);
    return `Removed ${entryCount} entries (${rh(totalHrs)}h) from ${staff}`;
  }

  // MOVEs (possibly with UPDATEs for hour adjustments)
  if (actions.has('MOVE')) {
    const moves = rows.filter(r => r.Action === 'MOVE');
    const oldDates = moves.filter(r => r.OldDate).map(r => r.OldDate);
    const newDates = moves.filter(r => r.NewDate).map(r => r.NewDate);
    let desc = `Shifted ${dateRange(oldDates)} → ${dateRange(newDates)} (${entryCount} entries)`;
    // Check for hour adjustments
    const updates = rows.filter(r => r.Action === 'UPDATE' && r.OldHours != null && r.NewHours != null);
    if (updates.length > 0) {
      const oldTotal = updates.reduce((s, r) => s + r.OldHours, 0);
      const newTotal = updates.reduce((s, r) => s + r.NewHours, 0);
      const delta = newTotal - oldTotal;
      if (Math.abs(delta) >= 0.1) {
        desc += `, ${delta > 0 ? '+' : ''}${rh(delta)}h adjustment`;
      }
    }
    return desc;
  }

  // JOB_CHANGEs
  if (actions.has('JOB_CHANGE') || rows.some(r => r.OldJobId && r.NewJobId && r.OldJobId !== r.NewJobId)) {
    const r = rows.find(r => r.OldJobId && r.NewJobId && r.OldJobId !== r.NewJobId) || rows[0];
    const from = r.OldJobClientName ? `${r.OldJobClientName} / ${r.OldJobName}` : (r.OldJobName || 'previous job');
    const to = r.NewJobClientName ? `${r.NewJobClientName} / ${r.NewJobName}` : (r.NewJobName || 'new job');
    const totalHrs = rows.reduce((s, r) => s + (r.NewHours || r.OldHours || 0), 0);
    return `${from} → ${to} (${entryCount} entries, ${rh(totalHrs)}h)`;
  }

  // REASSIGNs
  if (actions.has('REASSIGN')) {
    const r = rows.find(r => r.Action === 'REASSIGN');
    const oldName = r?.OldStaffName || 'previous staff';
    const newName = r?.StaffName || 'new staff';
    const totalHrs = rows.reduce((s, r) => s + (r.NewHours || r.OldHours || 0), 0);
    return `${oldName} → ${newName} (${entryCount} entries, ${rh(totalHrs)}h)`;
  }

  // UPDATEs only
  if (actions.size === 1 && actions.has('UPDATE')) {
    const withHours = rows.filter(r => r.OldHours != null && r.NewHours != null);
    if (withHours.length > 0) {
      const oldTotal = withHours.reduce((s, r) => s + r.OldHours, 0);
      const newTotal = withHours.reduce((s, r) => s + r.NewHours, 0);
      const dates = rows.filter(r => r.NewDate || r.OldDate).map(r => r.NewDate || r.OldDate);
      return `Adjusted hours on ${dateRange(dates)} (${entryCount} entries, ${rh(oldTotal)}h → ${rh(newTotal)}h)`;
    }
    return `Updated ${entryCount} entries`;
  }

  return `${entryCount} entries modified`;
}

// ── Derive all action types from a row's old/new field values ────────────────
// The Action column stores primary intent; this derives the full set from data.
function deriveActions(r) {
  if (isPh(r)) return [PH_ACTIONS.has(r.Action) ? r.Action : 'PH_UPDATE'];
  const actions = [];
  const hasOld = r.OldDate != null || r.OldHours != null || r.OldStaffId != null;
  const hasNew = r.NewDate != null || r.NewHours != null || r.NewStaffId != null;
  if (hasOld && !hasNew) return ['DELETE'];
  if (!hasOld && hasNew) return ['INSERT'];
  if (r.OldStaffId != null && r.NewStaffId != null && r.OldStaffId !== r.NewStaffId) actions.push('REASSIGN');
  if (r.OldJobId != null && r.NewJobId != null && r.OldJobId !== r.NewJobId) actions.push('JOB_CHANGE');
  if (r.OldDate && r.NewDate && String(r.OldDate).slice(0, 10) !== String(r.NewDate).slice(0, 10)) actions.push('MOVE');
  if (r.OldHours != null && r.NewHours != null && r.OldHours !== r.NewHours) actions.push('UPDATE');
  return actions.length > 0 ? actions : [r.Action || 'UPDATE'];
}

const ACTION_ORDER = ['DELETE', 'JOB_CHANGE', 'REASSIGN', 'MOVE', 'INSERT', 'UPDATE',
  'PH_CREATE', 'PH_UPDATE', 'PH_JOB_CHANGE', 'PH_DELETE'];

// ── Consolidate MOVE+UPDATE per EntryId within a batch ──────────────────────
// Merges rows for the same entry into a single display item. Placeholder rows have no
// EntryId, so they are keyed by PlaceholderId (otherwise every one would collapse into a
// single "null" group).
function consolidateRows(rows) {
  const byEntry = {};
  const order = [];
  for (const r of rows) {
    const key = r.EntryId != null ? `e${r.EntryId}` : `ph${r.PlaceholderId ?? r.AuditId}`;
    if (!byEntry[key]) { byEntry[key] = []; order.push(key); }
    byEntry[key].push(r);
  }
  const result = [];
  for (const key of order) {
    const group = byEntry[key];
    if (group.length === 1) {
      result.push(group[0]);
      continue;
    }
    // Merge MOVE + UPDATE for same entry into one consolidated row
    const move = group.find(r => r.Action === 'MOVE');
    const update = group.find(r => r.Action === 'UPDATE');
    if (move && update) {
      result.push({
        ...move,
        Action: 'MOVE',
        OldHours: update.OldHours ?? move.OldHours,
        NewHours: update.NewHours ?? move.NewHours,
        _consolidated: true,
      });
    } else {
      // Multiple rows but not MOVE+UPDATE — show first, note the rest
      result.push({ ...group[0], _extraCount: group.length - 1 });
    }
  }
  return result;
}

const PERSON_CAP = 3000;

// Every name a row carries — client, job (old/new), custom job, staff (current/previous) and
// who made the change — so one search box finds "Daniel" in a job history or "Acme" in a
// person history alike.
function rowSearchText(r) {
  return [
    r.ClientName, r.JobName, r.CustomJobName, r.OldJobName, r.NewJobName, r.OldJobClientName, r.NewJobClientName,
    r.StaffName, r.OldStaffName, r.ChangedByName, r.ChangedBy,
  ].filter(Boolean).join(' ').toLowerCase();
}

export default function ScheduleHistoryModal({ jobId, staffId, jobName, clientName, staffName, onClose }) {
  const personMode = !jobId && !!staffId;
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [expandedFull, setExpandedFull] = useState(new Set());
  // Filters: the change-date range is applied by the server (so the person view's row cap
  // counts only rows inside it); the name search filters the loaded rows as you type.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!jobId && !staffId) return;
    setLoading(true);
    const range = { ...(fromDate ? { fromDate } : {}), ...(toDate ? { toDate } : {}) };
    fetchScheduleHistory(personMode ? { staffId, ...range } : { jobId, ...range })
      .then(data => setHistory(data || []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [jobId, staffId, personMode, fromDate, toDate]);

  const needle = search.trim().toLowerCase();
  const shown = needle ? history.filter(r => rowSearchText(r).includes(needle)) : history;
  const filtersOn = !!(fromDate || toDate || needle);

  // Group rows by BatchId (null BatchId = standalone events grouped by AuditId)
  const batches = [];
  const batchMap = {};
  for (const row of shown) {
    const key = row.BatchId != null ? `b_${row.BatchId}` : `s_${row.AuditId}`;
    if (!batchMap[key]) {
      batchMap[key] = {
        key, batchId: row.BatchId, rows: [],
        changedBy: row.ChangedBy, changedByName: row.ChangedByName,
        changedAt: row.ChangedAt, source: row.Source, notes: row.Notes,
      };
      batches.push(batchMap[key]);
    }
    batchMap[key].rows.push(row);
    if (row.ChangedAt < batchMap[key].changedAt) batchMap[key].changedAt = row.ChangedAt;
  }

  // Collect all derived action types for a batch (no dependency on other functions)
  function batchDerivedActions(batch) {
    const all = new Set();
    for (const r of batch.rows) for (const a of deriveActions(r)) all.add(a);
    return all;
  }

  // Determine primary action — structural changes (JOB_CHANGE, REASSIGN) always win,
  // then source-based intent, then data-based fallback.
  function batchAction(batch) {
    const all = batchDerivedActions(batch);
    const src = batch.source;

    // Placeholder flows: the person-side change is the headline.
    if (src === 'placeholder-assign' && all.has('INSERT')) return 'INSERT';
    if (src === 'placeholder-convert' && all.has('DELETE')) return 'DELETE';
    if ([...all].every(a => PH_ACTIONS.has(a))) {
      return ACTION_ORDER.find(a => PH_ACTIONS.has(a) && all.has(a)) || 'PH_UPDATE';
    }

    // Structural changes always take priority regardless of source
    if (all.has('JOB_CHANGE')) return 'JOB_CHANGE';
    if (all.has('REASSIGN')) return 'REASSIGN';

    // Source-based intent for non-structural changes
    if (src === 'drag-drop') {
      if (all.has('MOVE')) return 'MOVE';
    }
    if (src === 'roll-forward') return 'INSERT';
    if (src === 'edit') return 'UPDATE';

    // Fallback: data-based hierarchy for unknown/generic sources
    if (all.has('MOVE')) return 'MOVE';
    if (all.has('DELETE') && !all.has('INSERT') && !all.has('MOVE')) return 'DELETE';
    if (all.has('INSERT') && !all.has('UPDATE')) return 'INSERT';
    return 'UPDATE';
  }

  // All distinct action types — primary first, then remaining in standard order
  function batchActions(batch) {
    const all = batchDerivedActions(batch);
    const primary = batchAction(batch);
    const rest = ACTION_ORDER.filter(a => all.has(a) && a !== primary);
    return [primary, ...rest];
  }

  const title = personMode ? `${staffName ? `${staffName}'s ` : ''}Schedule History` : 'Scheduling History';
  const subtitle = personMode
    ? 'Every change to their schedule — client jobs, internal jobs and placeholders'
    : <>{clientName} &middot; {jobName}</>;

  return (
    <div className="sched-mgr-modal-backdrop" onClick={onClose}>
      <div className="sched-mgr-modal" style={{ width: 720, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ background: BRAND.deepTeal, color: '#fff', margin: '-24px -24px 0', padding: '14px 24px', borderRadius: '14px 14px 0 0', flexShrink: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{title}</div>
          <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>
            {subtitle}
          </div>
          <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 14 }}>&times;</button>
        </div>

        {/* Filters: change-date range + name search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 24px', borderBottom: '1px solid #E5E7EB', flexShrink: 0, fontSize: 12, color: '#6B7280' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            Changed from
            <input type="date" value={fromDate} max={toDate || undefined} onChange={e => setFromDate(e.target.value)}
              style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #D1D5DB', borderRadius: 5 }} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            to
            <input type="date" value={toDate} min={fromDate || undefined} onChange={e => setToDate(e.target.value)}
              style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #D1D5DB', borderRadius: 5 }} />
          </label>
          <input type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder={personMode ? 'Search client, job or who made the change…' : 'Search staff or who made the change…'}
            style={{ flex: 1, minWidth: 180, fontSize: 12, padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: 5 }} />
          {filtersOn && (
            <button type="button" className="sched-mgr-pill" style={{ padding: '3px 10px', fontSize: 11 }}
              onClick={() => { setFromDate(''); setToDate(''); setSearch(''); }}>
              Clear
            </button>
          )}
        </div>

        {/* Timeline */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
          {loading && (
            <div style={{ textAlign: 'center', color: '#9CA3AF', padding: 32, fontSize: 13 }}>Loading history...</div>
          )}

          {!loading && batches.length === 0 && (
            <div style={{ textAlign: 'center', color: '#9CA3AF', padding: 32 }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>&#128203;</div>
              <div style={{ fontSize: 13 }}>
                {filtersOn ? 'No changes match these filters.' : `No scheduling history found for this ${personMode ? 'person' : 'job'}.`}
              </div>
            </div>
          )}

          {!loading && batches.map((batch, bi) => {
            const action = batchAction(batch);
            const allActions = batchActions(batch);
            const ac = ACTION_COLORS[action] || ACTION_COLORS.UPDATE;
            const isExpanded = expandedBatch === batch.key;
            const summary = summarizeBatch(batch.rows, batch.source);
            const sourceLabel = SOURCE_LABELS[batch.source] || batch.source || 'Unknown';
            const snapshot = batch.rows[0];
            const batchJob = personMode ? jobLabelOf(batch.rows.find(r => r.JobId != null || r.CustomJobId != null) || snapshot) : '';

            return (
              <div key={batch.key} style={{ position: 'relative', paddingLeft: 28, marginBottom: 4 }}>
                {/* Timeline line */}
                {bi < batches.length - 1 && (
                  <div style={{ position: 'absolute', left: 9, top: 20, bottom: -4, width: 2, background: '#E5E7EB' }} />
                )}
                {/* Timeline dot */}
                <div style={{
                  position: 'absolute', left: 2, top: 6, width: 16, height: 16, borderRadius: '50%',
                  background: ac.bg, border: `2px solid ${ac.text}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 800, color: ac.text,
                }}>
                  {ac.icon}
                </div>

                {/* Event card */}
                <div
                  style={{
                    padding: '10px 14px', borderRadius: 8, marginBottom: 6, cursor: 'pointer',
                    border: `1px solid ${isExpanded ? ac.text : '#E5E7EB'}`,
                    background: '#fff',
                    transition: 'border-color 0.15s',
                  }}
                  onClick={() => setExpandedBatch(isExpanded ? null : batch.key)}
                >
                  {/* Header row 1: action badges + source */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {allActions.map((a, ai) => {
                      const c = ACTION_COLORS[a] || ACTION_COLORS.UPDATE;
                      const isPrimary = ai === 0;
                      return (
                        <span key={a} style={{
                          fontSize: isPrimary ? 12 : 10, fontWeight: isPrimary ? 700 : 600,
                          color: c.text, padding: isPrimary ? '1px 6px' : '1px 5px',
                          background: c.bg, borderRadius: 4, opacity: isPrimary ? 1 : 0.8,
                        }}>
                          {c.label}
                        </span>
                      );
                    })}
                    <span style={{ fontSize: 10, color: '#9CA3AF', marginLeft: 'auto', padding: '1px 5px', background: '#F3F4F6', borderRadius: 3 }}>
                      {sourceLabel}
                    </span>
                  </div>
                  {/* Header row 2: timestamp + user */}
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3 }}>
                    {fmtDate(batch.changedAt)} at {fmtTime(batch.changedAt)}
                    <span style={{ color: '#9CA3AF' }}> &middot; </span>
                    by <span style={{ fontWeight: 600, color: '#374151' }}>{batch.changedByName || batch.changedBy || 'unknown'}</span>
                  </div>

                  {/* Person mode: which job this event was on */}
                  {personMode && batchJob && (
                    <div style={{ fontSize: 12, color: '#356371', marginTop: 3, fontWeight: 600 }}>{batchJob}</div>
                  )}

                  {/* Summary */}
                  <div style={{ fontSize: 13, color: '#374151', marginTop: 4, fontWeight: 500 }}>
                    {summary}
                  </div>

                  {/* Job snapshot stats (job mode — in person mode a batch's job total isn't the point) */}
                  {!personMode && snapshot?.TotalScheduledAtTime != null && (
                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                      Job at this point: {rh(snapshot.TotalScheduledAtTime)}h scheduled{snapshot.TotalWIPAtTime != null && `, ${rh(snapshot.TotalWIPAtTime)}h WIP`}
                    </div>
                  )}

                  {/* Notes */}
                  {batch.notes && (
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3, fontStyle: 'italic' }}>
                      &ldquo;{batch.notes}&rdquo;
                    </div>
                  )}

                  {/* Expanded details — consolidated rows */}
                  {isExpanded && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${ac.text}20` }}>
                      {(() => { const displayRows = consolidateRows(batch.rows); const limit = expandedFull.has(batch.key) ? displayRows.length : 20; return displayRows.slice(0, limit); })().map((r, ri) => {
                        const rowActions = deriveActions(r);
                        if (isPh(r)) {
                          return (
                            <div key={ri} style={{ fontSize: 12, color: '#6B7280', padding: '3px 0', display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                              {rowActions.map(a => {
                                const c = ACTION_COLORS[a] || ACTION_COLORS.PH_UPDATE;
                                return <span key={a} style={{ fontWeight: 600, color: c.text, fontSize: 10, padding: '0 4px', background: c.bg, borderRadius: 3 }}>{c.label}</span>;
                              })}
                              <span style={{ color: '#374151', fontStyle: 'italic' }}>Placeholder &middot; {phLevel(r)}</span>
                              {r.Action === 'PH_CREATE' && <span>{phRange(r, 'new')} &middot; {rh(r.NewHours)}h</span>}
                              {r.Action === 'PH_DELETE' && <span>{phRange(r, 'old')} &middot; {rh(r.OldHours)}h</span>}
                              {(r.Action === 'PH_UPDATE' || r.Action === 'PH_JOB_CHANGE') && (
                                <span>
                                  {phRange(r, 'old') !== phRange(r, 'new') ? <>{phRange(r, 'old')} &rarr; {phRange(r, 'new')}</> : phRange(r, 'new')}
                                  {' · '}
                                  {rh(r.OldHours) !== rh(r.NewHours) ? <>{rh(r.OldHours)}h &rarr; {rh(r.NewHours)}h</> : <>{rh(r.NewHours)}h</>}
                                </span>
                              )}
                              {personMode && <span style={{ color: '#9CA3AF' }}>{jobLabelOf(r)}</span>}
                            </div>
                          );
                        }
                        return (
                          <div key={ri} style={{ fontSize: 12, color: '#6B7280', padding: '3px 0', display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                            {/* Action badges — color-coded per derived type */}
                            {rowActions.map(a => {
                              const c = ACTION_COLORS[a] || ACTION_COLORS.UPDATE;
                              return <span key={a} style={{ fontWeight: 600, color: c.text, fontSize: 10, padding: '0 4px', background: c.bg, borderRadius: 3 }}>{c.label}</span>;
                            })}
                            <span style={{ color: '#374151' }}>
                              {personMode ? (jobLabelOf(r) || `Job #${r.JobId}`) : (r.StaffName || `Staff #${r.StaffId}`)}
                            </span>
                            {/* Date change — colored with MOVE color */}
                            {r.OldDate && r.NewDate && String(r.OldDate).slice(0,10) !== String(r.NewDate).slice(0,10) && (
                              <span style={{ color: ACTION_COLORS.MOVE.text }}>{fmtDateShort(r.OldDate)} &rarr; {fmtDateShort(r.NewDate)}</span>
                            )}
                            {r.OldDate && r.NewDate && String(r.OldDate).slice(0,10) === String(r.NewDate).slice(0,10) && (
                              <span>{fmtDateShort(r.NewDate)}</span>
                            )}
                            {r.OldDate && !r.NewDate && <span>{fmtDateShort(r.OldDate)}</span>}
                            {!r.OldDate && r.NewDate && <span>{fmtDateShort(r.NewDate)}</span>}
                            {/* Hours change */}
                            {r.OldHours != null && r.NewHours != null && r.OldHours !== r.NewHours && (
                              <span style={{ color: ACTION_COLORS.UPDATE.text }}>{rh(r.OldHours)}h &rarr; {rh(r.NewHours)}h</span>
                            )}
                            {r.OldHours != null && r.NewHours == null && <span>{rh(r.OldHours)}h</span>}
                            {r.OldHours == null && r.NewHours != null && <span>{rh(r.NewHours)}h</span>}
                            {r.OldHours != null && r.NewHours != null && r.OldHours === r.NewHours && (
                              <span>{rh(r.NewHours)}h</span>
                            )}
                            {/* Reassign from→to */}
                            {rowActions.includes('REASSIGN') && (
                              personMode
                                ? <span style={{ fontStyle: 'italic', color: ACTION_COLORS.REASSIGN.text }}>{r.OldStaffName || 'previous staff'} &rarr; {r.StaffName || 'new staff'}</span>
                                : r.OldStaffName && <span style={{ fontStyle: 'italic', color: ACTION_COLORS.REASSIGN.text }}>from {r.OldStaffName}</span>
                            )}
                            {/* Job change old→new */}
                            {rowActions.includes('JOB_CHANGE') && (r.OldJobName || r.NewJobName) && (
                              <span style={{ fontStyle: 'italic', color: ACTION_COLORS.JOB_CHANGE.text }}>
                                {r.OldJobClientName || ''} {r.OldJobName || '?'} &rarr; {r.NewJobClientName || ''} {r.NewJobName || '?'}
                              </span>
                            )}
                          </div>
                        );
                      })}
                      {batch.rows.length > 20 && !expandedFull.has(batch.key) && (
                        <div onClick={(e) => { e.stopPropagation(); setExpandedFull(prev => new Set(prev).add(batch.key)); }}
                          style={{ fontSize: 12, color: '#356371', marginTop: 4, cursor: 'pointer', textDecoration: 'underline' }}>
                          Show remaining {batch.rows.length - 20} entries
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid #E5E7EB', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>
            {batches.length} event{batches.length !== 1 ? 's' : ''} · {shown.length} audit rows
            {needle && shown.length !== history.length && ` (of ${history.length} loaded)`}
            {personMode && history.length >= PERSON_CAP && ` · showing the most recent ${PERSON_CAP.toLocaleString()}${fromDate || toDate ? ' in this range' : ' — narrow the dates to see older changes'}`}
          </span>
          <button className="sched-mgr-pill" onClick={onClose} style={{ padding: '6px 16px' }}>Close</button>
        </div>
      </div>
    </div>
  );
}
